import { format, addDays, formatKoreanWeekRange, formatKoreanFull } from '@/lib/dates';

import type { ViewMode } from '@/types';
import type { SyncStatus } from '@/hooks/useEvents';
import styles from './CalendarHeader.module.css';

interface Props {
  viewMode: ViewMode;
  currentDate: Date;
  selectedDate: Date;
  syncStatus: SyncStatus;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onPrevDay: () => void;
  onNextDay: () => void;
  onToday: () => void;
  onBack: () => void;
  onAdd: () => void;
}

export function CalendarHeader({
  viewMode, currentDate, selectedDate, syncStatus,
  onPrevMonth, onNextMonth,
  onPrevWeek, onNextWeek,
  onPrevDay, onNextDay,
  onToday, onBack, onAdd,
}: Props) {

  const syncDotClass = [
    styles.syncDot,
    syncStatus === 'syncing' ? styles.syncing : '',
    syncStatus === 'offline' ? styles.offline : '',
    syncStatus === 'error' ? styles.error : '',
  ].filter(Boolean).join(' ');

  const AddBtn = (
    <button className={styles.addBtn} onClick={onAdd} aria-label="새 일정 추가">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </button>
  );

  if (viewMode === 'month') {
    return (
      <header className={styles.header}>
        <div className={styles.titleGroup}>
          <h1 className={styles.month}>{format(currentDate, 'MMMM')}</h1>
          <span className={styles.year}>{format(currentDate, 'yyyy')}</span>
          <div className={syncDotClass} title={syncStatus} />
        </div>
        <div className={styles.controls}>
          <button className={styles.navBtn} onClick={onPrevMonth} aria-label="이전 달"><ChevronLeft /></button>
          <button className={styles.todayBtn} onClick={onToday}>오늘</button>
          <button className={styles.navBtn} onClick={onNextMonth} aria-label="다음 달"><ChevronRight /></button>
          {AddBtn}
        </div>
      </header>
    );
  }

  if (viewMode === 'week') {
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(selectedDate, i));
    return (
      <header className={styles.header}>
        <div className={styles.titleGroup}>
          <button className={styles.backBtn} onClick={onBack}><ChevronLeft /><span>월</span></button>
          <span className={styles.viewLabel}>{formatKoreanWeekRange(weekDays)}</span>
          <div className={syncDotClass} title={syncStatus} />
        </div>
        <div className={styles.controls}>
          <button className={styles.navBtn} onClick={onPrevWeek} aria-label="이전 주"><ChevronLeft /></button>
          <button className={styles.todayBtn} onClick={onToday}>오늘</button>
          <button className={styles.navBtn} onClick={onNextWeek} aria-label="다음 주"><ChevronRight /></button>
          {AddBtn}
        </div>
      </header>
    );
  }

  return (
    <header className={styles.header}>
      <div className={styles.titleGroup}>
        <button className={styles.backBtn} onClick={onBack}><ChevronLeft /><span>주</span></button>
        <span className={styles.viewLabel}>{formatKoreanFull(selectedDate)}</span>
        <div className={syncDotClass} title={syncStatus} />
      </div>
      <div className={styles.controls}>
        <button className={styles.navBtn} onClick={onPrevDay} aria-label="이전 날"><ChevronLeft /></button>
        <button className={styles.todayBtn} onClick={onToday}>오늘</button>
        <button className={styles.navBtn} onClick={onNextDay} aria-label="다음 날"><ChevronRight /></button>
        {AddBtn}
      </div>
    </header>
  );
}

function ChevronLeft() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M11 13.5L6.5 9L11 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function ChevronRight() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M7 4.5L11.5 9L7 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
